import styled from 'styled-components'
import { t } from '@stokr/components-library'
import { ChartTabs } from 'components/ui/chart'
import { stoMedia } from 'constants/stoTheme'
import type { ChartRange } from 'hooks/sto/useChartRange'
import type { ChartRangeId } from 'models/chart'
import { useStoTrack } from 'hooks/sto/useStoTracking'

/**
 * Rendered twice in a chart: in the header on desktop, under the chart on mobile (`below`),
 * centred there; too many tabs for the width take the full width and scroll. Each copy hides
 * at the other size.
 */
const Slot = styled.div<{ $placement: 'header' | 'below' }>`
  ${({ $placement }) =>
    $placement === 'below' &&
    `
    display: flex;
    justify-content: center;
    /* 16px above and below, with the chart column's 8px gap. */
    padding: 8px 0;

    > * {
      min-width: 0;
    }
  `}

  ${({ $placement }) => ($placement === 'header' ? stoMedia.belowDesktop : stoMedia.desktopUp)} {
    display: none;
  }
`

interface RangePickerProps {
  range: ChartRange
  /** Which chart, for analytics and `data-cy`: `hero` or `performance`. */
  chart: 'hero' | 'performance'
  placement?: 'header' | 'below'
}

/** The range tabs (1D … All) for a `useChartRange` state; hidden when there is nothing to pick. */
export const RangePicker = ({ range, chart, placement }: RangePickerProps) => {
  const trackSto = useStoTrack()
  if (range.ranges.length <= 1) return null
  const tabs = (
    <ChartTabs<ChartRangeId>
      options={range.ranges.map((id) => ({ value: id, label: id }))}
      value={range.active}
      onChange={(id) => {
        if (id !== range.active) trackSto('sto_chart_range_selected', { chart, range: id })
        range.select(id)
      }}
      label={t('stoPage.heroChart.rangeLabel')}
      dataCy={`sto-${chart}-chart-range`}
    />
  )
  return placement ? <Slot $placement={placement}>{tabs}</Slot> : tabs
}
